import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Archive, ChevronDown, ChevronUp, Plus, MoreVertical, Info, Settings, Trash2, Eye, EyeOff, Move, Tag } from 'lucide-react';
import NoteSettings from './NoteSettings';
import { noteCategories } from './noteCategories';
import EditableText from './EditableText';
import CategorySelector from './CategorySelector';
import TaskActionConfirmationModal from './TaskActionConfirmationModal';
import { stickyNoteStyles } from './componentStyles';
import TaskItem from './TaskItem';
import { getGlowColor } from './noteStyles';
import { getCollectionForTaskType } from '../hooks/useFirebase';
import { RecurringTaskService } from '../services/recurringTaskService';
import TaskInfoModal from './TaskInfoModal';
import { getNextTaskId, createTaskId } from '../utils/taskIdUtils';

const MAX_TITLE_LENGTH = 20;

const StickyNote = ({
  id,
  relativeX,
  relativeY,
  subtasks,
  color,
  onChangeColor,
  quadrant,
  onChangeQuadrant,
  quadrantColors,
  onMove,
  createdAt,
  dueAt,
  onUpdateTimes,
  onUpdateContent,
  category,
  onUpdateCategory,
  title,
  onUpdateTitle,
  zIndex,
  onNoteClick,
  isExpanded = true,
  onToggleExpand,
  hideCompleted = false,
  onUpdateHideCompleted,
  isLastClicked = false,
  completedDates = [],
  taskType = 'active',
  onTaskAction,
  assignedDays = [],
  scheduledFor,
}) => {

  const recurringTaskService = useMemo(() =>
    new RecurringTaskService(
      (id, updates, collection) => onTaskAction(id, 'update', collection, updates)
    ),
    [onTaskAction]
  );

  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingWithButton, setIsDraggingWithButton] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [localPosition, setLocalPosition] = useState({ x: relativeX, y: relativeY });
  const [isHovered, setIsHovered] = useState(false);
  const noteRef = useRef(null);
  const quadrantRef = useRef(null);
  const dragTimeoutRef = useRef(null);
  const isDraggingRef = useRef(false);
  const [showActionConfirm, setShowActionConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const isPastDue = dueAt && new Date() > new Date(dueAt);
  const [noteCategory, setNoteCategory] = useState(category || "Other");
  const [showMenu, setShowMenu] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const menuRef = useRef(null);

  const [checklist, setChecklist] = useState(() => {
    // Ensure subtasks is an array
    if (Array.isArray(subtasks)) {
      return subtasks;
    } else if (typeof subtasks === 'string') {
      try {
        return JSON.parse(subtasks);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  const [localHideCompleted, setLocalHideCompleted] = useState(hideCompleted);
  // const [completedDates, setCompletedDates] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [metrics, setMetrics] = useState({
    totalCompletions: 0,
    completionRate: 0,
    currentStreak: 0,
    longestStreak: 0,
    lastCompletedAt: null
  });

  useEffect(() => {
    if (taskType === 'recurring') {
      const newIsCompleted = checklist.length > 0 && checklist.every(item => item.checked);
      if (newIsCompleted !== isCompleted) {
        setIsCompleted(newIsCompleted);
        // Update completion status in the database
        recurringTaskService.updateTaskCompletion(
          id,
          checklist,
          assignedDays
        ).catch(error => {
          console.error('Error updating completion state:', error);
        });
      }
    }
  }, [checklist, taskType, isCompleted, id, assignedDays]);

  const isCompletedToday = useMemo(() => {
    if (taskType !== 'recurring') return false;
    const today = new Date().toISOString().split('T')[0];
    return completedDates.includes(today);
  }, [completedDates, taskType]);

  const renderCompletionStatus = () => {
    const completedCount = checklist.filter(item => item.checked).length;
    const total = checklist.length;

    if (total === 0) return null;

    return (
      <div
        className={`text-xs mt-2 ${isCompletedToday
          ? 'text-green-600 font-semibold'
          : 'text-gray-500'
          }`}
      >
        {isCompletedToday && (
          <span className="mr-1">✓</span>
        )}
        Completed {completedCount}/{total} Subtasks
      </div>
    );
  };

  const [showCategoryOverlay, setShowCategoryOverlay] = useState(false);
  const categoryOverlayRef = useRef(null);

  const handleActionClick = (action) => {
    setPendingAction(action);
    setShowActionConfirm(true);
    setShowMenu(false);
  };

  const handleActionConfirm = () => {
    onTaskAction(id, pendingAction, getCollectionForTaskType(taskType));
    setShowActionConfirm(false);
    setPendingAction(null);
  };

  const handleActionCancel = () => {
    setShowActionConfirm(false);
    setPendingAction(null);
  };

  const getMenuItems = () => {
    const baseItems = [
      {
        icon: Info,
        label: 'Information',
        onClick: handleInfoClick,
        className: 'text-gray-700'
      },
      {
        icon: Settings,
        label: 'Settings',
        onClick: handleSettingsClick,
        className: 'text-gray-700'
      }
    ];

    if (taskType === 'active') {
      baseItems.push({
        icon: localHideCompleted ? Eye : EyeOff,
        label: localHideCompleted ? 'Show Completed' : 'Hide Completed',
        onClick: toggleHideCompleted,
        className: 'text-gray-700'
      });
    }

    // Add Archive and Delete options
    baseItems.push(
      {
        icon: Archive,
        label: 'Archive Task',
        onClick: () => handleActionClick('archive'),
        className: 'text-green-600'
      },
      {
        icon: Trash2,
        label: 'Delete Task',
        onClick: () => handleActionClick('delete'),
        className: 'text-red-600'
      }
    );

    return baseItems;
  };


  const getCategoryIcon = () => {
    const categoryObj = noteCategories.find(cat => cat.name === noteCategory);
    return categoryObj ? categoryObj.icon : null;
  };

  const handleCategoryClick = (e) => {
    e.stopPropagation();
    setShowCategoryOverlay(true);
  };

  const handleCategorySelect = (newCategory) => {
    setNoteCategory(newCategory);
    onUpdateCategory(id, newCategory);
    setShowCategoryOverlay(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryOverlayRef.current && !categoryOverlayRef.current.contains(event.target)) {
        setShowCategoryOverlay(false);
      }
    };

    if (showCategoryOverlay) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showCategoryOverlay]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Update subtasks directly without stringifying
    onUpdateContent(id, checklist);
  }, [checklist]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showMenu) {
          setShowMenu(false);
        }
        if (showSettings) {
          setShowSettings(false);
        }
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        }
      }
      if (e.key === 'Enter' && showDeleteConfirm) {
        handleDeleteConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMenu, showSettings, showDeleteConfirm]);

  const toggleHideCompleted = () => {
    const newHideCompleted = !localHideCompleted;
    setLocalHideCompleted(newHideCompleted);
    onUpdateHideCompleted(id, newHideCompleted);
  };

  const visibleChecklist = localHideCompleted
    ? checklist.filter(item => !item.checked)
    : checklist;

  const completedCount = checklist.filter(item => item.checked).length;

  const toggleChecklistItem = async (itemId) => {
    const newChecklist = checklist.map(item =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    );
    setChecklist(newChecklist);

    // For non-recurring tasks, update content directly
    if (taskType !== 'recurring') {
      onUpdateContent(id, newChecklist);
    }
    // For recurring tasks, the useEffect above will handle the update
  };

  const addChecklistItem = () => {
    const nextId = getNextTaskId(checklist);
    const newItem = {
      id: createTaskId(nextId),
      text: '',
      checked: false
    };
    setChecklist([...checklist, newItem]);
  };

  const deleteChecklistItem = (itemId) => {
    setChecklist(checklist.filter(item => item.id !== itemId));
  };

  const editChecklistItem = (itemId, newText) => {
    setChecklist(checklist.map(item =>
      item.id === itemId ? { ...item, text: newText } : item
    ));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Update the content whenever the checklist changes
    onUpdateContent(id, JSON.stringify(checklist));
  }, [checklist]);

  useEffect(() => {
    // Find the quadrant element
    const quadrantElement = noteRef.current.closest('.quadrant');
    if (quadrantElement) {
      quadrantRef.current = quadrantElement;
    }
  }, []);

  useEffect(() => {
    setLocalPosition({ x: relativeX, y: relativeY });
  }, [relativeX, relativeY, id]);

  const startDragging = useCallback((clientX, clientY) => {
    setIsDragging(true);
    isDraggingRef.current = true;
    const quadrantRect = quadrantRef.current.getBoundingClientRect();
    setStartPosition({
      x: clientX - quadrantRect.left - localPosition.x * quadrantRect.width,
      y: clientY - quadrantRect.top - localPosition.y * quadrantRect.height
    });
    document.body.style.userSelect = 'none';
    // Prevent elastic scrolling on iOS
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    // Prevent pull-to-refresh
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  }, [localPosition]);

  const handleDragging = useCallback((clientX, clientY) => {
    if ((isDragging || isDraggingWithButton) && quadrantRef.current) {
      const quadrantRect = quadrantRef.current.getBoundingClientRect();
      const noteRect = noteRef.current.getBoundingClientRect();

      let newX = (clientX - quadrantRect.left - startPosition.x) / quadrantRect.width;
      let newY = (clientY - quadrantRect.top - startPosition.y) / quadrantRect.height;

      newX = Math.max(0, Math.min(1 - noteRect.width / quadrantRect.width, newX));
      newY = Math.max(0, Math.min(1 - noteRect.height / quadrantRect.height, newY));

      setLocalPosition({ x: newX, y: newY });
    }
  }, [isDragging, isDraggingWithButton, id, startPosition]);

  const stopDragging = useCallback(() => {
    if (isDragging || isDraggingWithButton) {
      setIsDragging(false);
      setIsDraggingWithButton(false);
      isDraggingRef.current = false;
      document.body.style.userSelect = '';
      // Restore normal scrolling
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';

      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }

      dragTimeoutRef.current = setTimeout(() => {
        onMove(id, localPosition.x, localPosition.y);
      }, 5);
    }
  }, [isDragging, isDraggingWithButton, id, localPosition, onMove]);

  // Mouse event handlers
  const handleMouseDown = useCallback((e) => {
    if (e.target === noteRef.current || e.target.classList.contains('drag-handle')) {
      e.preventDefault();
      startDragging(e.clientX, e.clientY);
      onNoteClick(id);
    }
  }, [startDragging, onNoteClick, id]);

  const handleMouseMove = useCallback((e) => {
    handleDragging(e.clientX, e.clientY);
  }, [handleDragging]);

  const handleMouseUp = useCallback(() => {
    stopDragging();
  }, [stopDragging]);

  // Touch event handlers
  const handleTouchStart = useCallback((e) => {
    if (e.target === noteRef.current || e.target.classList.contains('drag-handle')) {
      e.preventDefault();
      const touch = e.touches[0];
      startDragging(touch.clientX, touch.clientY);
      onNoteClick(id);
    }
  }, [startDragging, onNoteClick, id]);

  const handleTouchMove = useCallback((e) => {
    if (isDraggingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      handleDragging(touch.clientX, touch.clientY);
    }
  }, [handleDragging]);

  const handleTouchEnd = useCallback((e) => {
    if (isDraggingRef.current) {
      e.preventDefault();
      stopDragging();
    }
  }, [stopDragging]);

  const handleTouchCancel = useCallback((e) => {
    if (isDraggingRef.current) {
      e.preventDefault();
      stopDragging();
    }
  }, [stopDragging]);

  const handleMoveButtonTouchStart = useCallback((e) => {
    e.stopPropagation();
    setIsDraggingWithButton(true);
    const touch = e.touches[0];
    startDragging(touch.clientX, touch.clientY);
  }, [startDragging]);

  const handleMoveButtonMouseDown = useCallback((e) => {
    e.stopPropagation();
    setIsDraggingWithButton(true);
    startDragging(e.clientX, e.clientY);
  }, [startDragging]);

  useEffect(() => {
    if (isDragging || isDraggingWithButton) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      window.addEventListener('touchcancel', handleTouchCancel);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [
    isDragging,
    isDraggingWithButton,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel
  ]);

  useEffect(() => {
    return () => {
      if (isDraggingRef.current) {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.userSelect = '';
      }
    };
  }, []);

  const handleNoteClick = (e) => {
    if (e.target === noteRef.current) {
      onNoteClick(id);
    }
  };

  const handleToggleExpand = () => {
    onToggleExpand(id, !isExpanded);
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleInfoClick = () => {
    setShowInfo(true);
    setShowMenu(false);
  };

  const handleSettingsClick = () => {
    setShowSettings(true);
    setShowMenu(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    setShowMenu(false);
  };

  const handleDeleteConfirm = () => {
    onTaskAction(id, 'delete', getCollectionForTaskType(taskType));
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleSettingsSave = (newSettings) => {
    onChangeColor(id, { color: newSettings.color });
    onChangeQuadrant(id, newSettings.quadrant);
    onUpdateCategory(id, newSettings.category);
    setNoteCategory(newSettings.category);
    onUpdateTimes(id, {
      dueAt: newSettings.dueAt
    });
  };

  const glowColor = getGlowColor(color, isPastDue);

  return (
    <>
      <div
        ref={noteRef}
        className={`${color} transition-shadow duration-300`}
        style={{
          ...stickyNoteStyles.note,
          left: `${localPosition.x * 100}%`,
          top: `${localPosition.y * 100}%`,
          zIndex: isDragging || isDraggingWithButton ? 1000 : isLastClicked ? 9 : zIndex,
          boxShadow: isLastClicked
            ? `0 0 20px ${glowColor}, 0 0 40px ${glowColor}`
            : isHovered || isDragging || isDraggingWithButton
              ? '0 4px 8px rgba(0, 0, 0, 0.2)'
              : '0 2px 4px rgba(0, 0, 0, 0.1)',
          border: isLastClicked
            ? `2px solid ${glowColor}`
            : isPastDue
              ? '2px solid rgba(239, 68, 68, 0.5)'
              : 'none',
          opacity: isDragging || isDraggingWithButton ? 0.8 : 1,
          transform: isLastClicked ? 'scale(1.02)' : 'scale(1)',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.3s ease-in-out, border 0.3s ease-in-out',
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          handleNoteClick(e);
          onNoteClick(id);
        }}
      >
        <div
          className="drag-handle"
          style={{
            ...stickyNoteStyles.dragHandle,
            backgroundColor: isHovered || isDragging ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
          }}
        ></div>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start flex-grow overflow-hidden">
            <div
              className="flex-shrink-0 mr-3 text-gray-600 cursor-pointer"
              onClick={handleCategoryClick}
            >
              {getCategoryIcon()}
            </div>
            <div className="flex-grow mr-2">
              <EditableText
                text={title}
                maxLength={MAX_TITLE_LENGTH}
                onSave={(newTitle) => onUpdateTitle(id, newTitle)}
                onCancel={() => { }}
                label="Task Title"
                color={color}
              />
            </div>
          </div>
          <div className="flex-shrink-0 ml-2">
            <button
              className="text-gray-600 hover:text-gray-800 mr-1"
              onClick={handleToggleExpand}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            <div className="relative inline-block" ref={menuRef}>
              <button
                className="text-gray-600 hover:text-gray-800"
                onClick={toggleMenu}
              >
                <MoreVertical size={16} />
              </button>
              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                  {getMenuItems().map((item, index) => (
                    <button
                      key={index}
                      className={`flex items-center px-4 py-2 text-sm ${item.className} hover:bg-gray-100 w-full`}
                      onClick={item.onClick}
                    >
                      <item.icon size={16} className="mr-2" /> {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <>
            <div className="mb-2 mt-2 max-h-60 overflow-y-auto">
              {visibleChecklist.map((item) => (
                <TaskItem
                  key={item.id}
                  item={item}
                  onToggle={toggleChecklistItem}
                  onDelete={deleteChecklistItem}
                  onEdit={editChecklistItem}
                  color={color}
                />
              ))}
              {(localHideCompleted || taskType === 'recurring') && checklist.length > 0 && (
                renderCompletionStatus()
              )}
            </div>
            <div className="flex items-center justify-between">
              <button
                onClick={addChecklistItem}
                className="text-left text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <Plus size={16} className="inline mr-1" /> Add subtask
              </button>
              {isMobile && (
                <button
                  className="drag-handle p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  onMouseDown={handleMoveButtonMouseDown}
                  onTouchStart={handleMoveButtonTouchStart}
                >
                  <Move size={20} />
                </button>
              )}
            </div>
            {dueAt && (
              <div className={`mt-2 text-xs ${isPastDue ? 'text-red-500' : 'text-gray-600'}`}>
                Due: {new Date(dueAt).toLocaleString()}
              </div>
            )}
          </>
        )}
      </div>

      <TaskInfoModal
        isOpen={showInfo}
        onClose={() => setShowInfo(false)}
        noteCategory={noteCategory}
        createdAt={createdAt}
        taskType={taskType}
        scheduledFor={scheduledFor}
        dueAt={dueAt}
        metrics={metrics}
        assignedDays={assignedDays}
      />

      <NoteSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        note={{
          id,
          color,
          quadrant,
          category,
          dueAt
        }}
        onSave={handleSettingsSave}
        quadrantColors={quadrantColors}
      />

      {showActionConfirm && (
        <TaskActionConfirmationModal
          isOpen={showActionConfirm}
          onClose={handleActionCancel}
          onConfirm={handleActionConfirm}
          action={pendingAction}
        />
      )}

      {showCategoryOverlay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={categoryOverlayRef}
            className="bg-purple-50 rounded-lg shadow-lg z-50 p-6"
            style={{ minWidth: '300px', maxWidth: '90%' }}
          >
            <label className="flex items-center mb-2 text-lg font-medium text-black">
              <Tag size={20} className="mr-2" />
              Category
            </label>
            <CategorySelector
              selectedCategory={noteCategory}
              onSelectCategory={handleCategorySelect}
              maxWidth={300}
            />
            <button
              className="mt-4 px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
              onClick={() => setShowCategoryOverlay(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default StickyNote;